import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddSubscriptionModal from '../components/AddSubscriptionModal';
import Pagination from '../components/Pagination';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';

const METHOD_ICONS = { cash: '💵', card: '💳', transfer: '🏦' };
const PAGE_SIZES   = [10, 25, 50];

function StatusBadge({ status, t }) {
  if (status === 'paid')    return <span className="badge badge-green">✓ {t('paid')}</span>;
  if (status === 'partial') return <span className="badge" style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--accent)' }}>◑ {t('partial')}</span>;
  return <span className="badge badge-red">✗ {t('unpaid')}</span>;
}

// Safe number formatter — returns '0' instead of crashing on undefined/null
const fmt = (n) => (n != null && !isNaN(n) ? Number(n).toLocaleString() : '0');

export default function RestaurantDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { t }        = useSettings();
  const { isSuperadmin } = useAuth();

  const [restaurant,    setRestaurant]    = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [calendar,      setCalendar]      = useState([]);
  const [report,        setReport]        = useState(null);
  const [calYear,       setCalYear]       = useState(new Date().getFullYear());
  const [tab,           setTab]           = useState('history');
  const [showModal,     setShowModal]     = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(10);

  useEffect(() => { setPage(1); }, [tab, pageSize]);

  // Normalize old subscription documents that lack the new partial-payment fields
  const normalizeSub = (s) => {
    const total     = s.totalAmount     ?? s.amount ?? 0;
    const paid      = s.paidAmount      ?? s.amount ?? 0;
    const remaining = s.remainingAmount ?? 0;
    const status    = s.paymentStatus   ?? (paid >= total && total > 0 ? 'paid' : 'unpaid');
    return { ...s, totalAmount: total, paidAmount: paid, remainingAmount: remaining, paymentStatus: status };
  };

  const fetchAll = useCallback(async () => {
    try {
      const [rRes, sRes, cRes, repRes] = await Promise.all([
        axios.get(`/api/restaurants/${id}`),
        axios.get(`/api/restaurants/${id}/subscriptions`),
        axios.get(`/api/restaurants/${id}/calendar?year=${calYear}`),
        axios.get(`/api/restaurants/${id}/report`),
      ]);
      setRestaurant(rRes.data);
      setSubscriptions(sRes.data.map(normalizeSub));
      setCalendar(cRes.data);
      setReport(repRes.data);
    } catch (err) {
      if (err.response?.status === 404) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, calYear, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (subId) => {
    if (!window.confirm(t('confirm_delete'))) return;
    await axios.delete(`/api/subscriptions/${subId}`);
    fetchAll();
  };

  const months      = t('months');
  const monthsShort = t('monthsShort');

  const totalHistoryPages = Math.max(1, Math.ceil(subscriptions.length / pageSize));
  const pagedSubs = useMemo(
    () => subscriptions.slice((page - 1) * pageSize, page * pageSize),
    [subscriptions, page, pageSize]
  );

  // Calendar color helpers
  const calColor = (s) => {
    if (s === 'paid')    return { bg: 'var(--green-bg)',  border: 'rgba(76,175,130,0.25)',  dot: 'var(--green)' };
    if (s === 'partial') return { bg: 'rgba(201,168,76,0.1)', border: 'rgba(201,168,76,0.3)', dot: 'var(--accent)' };
    if (s === 'overdue') return { bg: 'var(--red-bg)',    border: 'rgba(224,92,92,0.25)',   dot: 'var(--red)' };
    return               { bg: 'var(--gray-bg)',   border: 'var(--border)',           dot: 'var(--text-dim)' };
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>{t('loading')}</div>;
  if (!restaurant) return null;

  return (
    <div>
      {/* Back + header */}
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← {t('back')}
        </button>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 30, marginBottom: 8, wordBreak: 'break-word' }}>{restaurant.name}</h1>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              {restaurant.planId && <span className="badge badge-gold">{restaurant.planId.name || restaurant.planId}</span>}
              {restaurant.phoneNumber && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>📞 {restaurant.phoneNumber}</span>}
              {restaurant.address && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>📍 {restaurant.address}</span>}
            </div>
          </div>
          <button className="btn-primary" onClick={() => setShowModal(true)} style={{ flexShrink: 0 }}>
            {t('recordPaymentBtn')}
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="stats-grid" style={{ marginBottom: 28 }}>
        {[
          { label: t('totalPayments'),    value: subscriptions.length,                        color: 'var(--accent)' },
          { label: t('collected'),        value: fmt(report?.collected) + ' UZS', color: 'var(--green)' },
          { label: t('totalOutstanding'), value: fmt(report?.outstanding) + ' UZS', color: report?.outstanding > 0 ? 'var(--red)' : 'var(--text-muted)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: s.label === t('memberSince') ? 16 : 20, fontFamily: 'DM Serif Display', color: s.color, marginBottom: 4, wordBreak: 'break-word' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {[['history', t('paymentHistory')], ['calendar', t('calendarView')], ['report', t('reportTitle')]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', whiteSpace: 'nowrap',
            color: tab === key ? 'var(--accent)' : 'var(--text-muted)',
            padding: '10px 18px', fontSize: 14, fontWeight: tab === key ? 600 : 400,
            borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1, transition: 'all 0.15s',
          }}>{label}</button>
        ))}
      </div>

      {/* ── History Tab ── */}
      {tab === 'history' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Rows per page */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('rowsPerPage')}:</span>
            <select className="select-control" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: 70 }}>
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {subscriptions.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('noPayments')}</div>
          ) : (
            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Period', t('paymentStatus'), t('paidAmount'), t('totalAmount'), t('remainingAmount'), t('paymentMethod'), t('invoiceNumber'), t('recordedBy'), t('date'), ''].map((h, i) => (
                      <th key={i} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedSubs.map(s => (
                    <tr key={s._id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--table-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 14px', fontWeight: 500, whiteSpace: 'nowrap' }}>{months[s.month - 1]} {s.year}</td>
                      <td style={{ padding: '12px 14px' }}><StatusBadge status={s.paymentStatus} t={t} /></td>
                      <td style={{ padding: '12px 14px', color: 'var(--green)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(s.paidAmount)} UZS</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>{fmt(s.totalAmount)} UZS</td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ color: s.remainingAmount > 0 ? 'var(--red)' : 'var(--text-dim)', fontSize: 13 }}>
                          {s.remainingAmount > 0 ? fmt(s.remainingAmount) + ' UZS' : '—'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>{METHOD_ICONS[s.paymentMethod]} {t(s.paymentMethod)}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13 }}>{s.invoiceNumber || '—'}</td>
                      <td style={{ padding: '12px 14px', fontSize: 13 }}><span className="badge badge-gray">{s.recordedBy?.username || '—'}</span></td>
                      <td style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>{new Date(s.paidAt).toLocaleDateString('en-GB')}</td>
                      <td style={{ padding: '12px 14px' }}>
                        {isSuperadmin
                          ? <button className="btn-danger" onClick={() => handleDelete(s._id)}>{t('delete')}</button>
                          : <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>—</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={page} totalPages={totalHistoryPages} total={subscriptions.length} pageSize={pageSize} onPage={setPage} />
        </div>
      )}

      {/* ── Calendar Tab ── */}
      {tab === 'calendar' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn-ghost" style={{ padding: '7px 13px', fontSize: 13 }} onClick={() => setCalYear(y => y - 1)}>← {calYear - 1}</button>
            <span style={{ fontFamily: 'DM Serif Display', fontSize: 20 }}>{calYear}</span>
            <button className="btn-ghost" style={{ padding: '7px 13px', fontSize: 13 }} onClick={() => setCalYear(y => y + 1)}>{calYear + 1} →</button>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginLeft: 'auto', fontSize: 12 }}>
              {[['paid', 'var(--green)', t('paid_cal')], ['partial', 'var(--accent)', t('partial_cal')], ['overdue', 'var(--red)', t('overdue_cal')], ['no-data', 'var(--text-dim)', t('noData_cal')]].map(([s, c, l]) => (
                <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-muted)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: c, display: 'inline-block', flexShrink: 0 }} />{l}
                </span>
              ))}
            </div>
          </div>

          <div className="calendar-grid">
            {calendar.map(({ month, status, subscription: sub }) => {
              const c = calColor(status);
              return (
                <div key={month} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 'var(--radius)', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{monthsShort[month - 1]}</span>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, display: 'inline-block', flexShrink: 0 }} />
                  </div>
                  {sub ? (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, color: sub.paymentStatus === 'paid' ? 'var(--green)' : sub.paymentStatus === 'partial' ? 'var(--accent)' : 'var(--red)' }}>
                        {fmt(sub.paidAmount)} <span style={{ fontSize: 10 }}>UZS</span>
                      </div>
                      {sub.remainingAmount > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 2 }}>
                          -{fmt(sub.remainingAmount)} left
                        </div>
                      )}
                      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{METHOD_ICONS[sub.paymentMethod]} {t(sub.paymentMethod)}</div>
                    </div>
                  ) : (
                    <div style={{ color: c.dot, fontSize: 12 }}>
                      {status === 'overdue' ? t('overdue_cal') : t('noData_cal')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Report Tab ── */}
      {tab === 'report' && report && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { label: t('totalBilled'),      value: fmt(report.total) + ' UZS',       color: 'var(--text)' },
              { label: t('collected'),        value: fmt(report.collected) + ' UZS',   color: 'var(--green)' },
              { label: t('totalOutstanding'), value: fmt(report.outstanding) + ' UZS', color: report.outstanding > 0 ? 'var(--red)' : 'var(--text-muted)' },
              { label: t('totalPayments'),    value: report.count,                                  color: 'var(--accent)' },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding: '20px 24px' }}>
                <div style={{ fontSize: 22, fontFamily: 'DM Serif Display', color: s.color, marginBottom: 4, wordBreak: 'break-word' }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Status breakdown */}
          <div className="card">
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 20 }}>
              Payment Status Breakdown
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {[
                { key: 'paid',    label: t('paidMonths'),    color: 'var(--green)',  bg: 'var(--green-bg)' },
                { key: 'partial', label: t('partialMonths'), color: 'var(--accent)', bg: 'rgba(201,168,76,0.12)' },
                { key: 'unpaid',  label: t('unpaidMonths'),  color: 'var(--red)',    bg: 'var(--red-bg)' },
              ].map(({ key, label, color, bg }) => (
                <div key={key} style={{ flex: 1, minWidth: 100, background: bg, borderRadius: 'var(--radius-sm)', padding: '16px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontFamily: 'DM Serif Display', color, marginBottom: 6 }}>{report.byStatus[key] || 0}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Simple visual bar */}
            {report.count > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ height: 10, borderRadius: 5, overflow: 'hidden', display: 'flex', gap: 2 }}>
                  {[
                    { key: 'paid',    color: 'var(--green)' },
                    { key: 'partial', color: 'var(--accent)' },
                    { key: 'unpaid',  color: 'var(--red)' },
                  ].map(({ key, color }) => {
                    const pct = ((report.byStatus[key] || 0) / report.count) * 100;
                    return pct > 0 ? (
                      <div key={key} style={{ width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.5s ease' }} title={`${key}: ${pct.toFixed(0)}%`} />
                    ) : null;
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
                  {report.count} {t('totalPayments').toLowerCase()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <AddSubscriptionModal
          restaurant={restaurant}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetchAll(); }}
        />
      )}
    </div>
  );
}
